import React, { useState } from 'react';
import {
  useQueueStats,
  useFailedJobs,
  useCoverageStats,
  useCostStats,
  useEnrichReporter,
  useEnrichPublication,
  type QueueStat,
  type FailedJob,
} from '../hooks/useAdmin';

function StatCard({
  label,
  value,
  sub,
  color = 'text-gray-900',
}: {
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function PctBar({ pct, color = 'bg-blue-500' }: { pct: number; color?: string }) {
  return (
    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
      <div
        className={`${color} h-1.5 rounded-full transition-all`}
        style={{ width: `${Math.min(100, pct)}%` }}
      />
    </div>
  );
}

function QueueRow({ queue }: { queue: QueueStat }) {
  const hasAlert = queue.failed > 10 || queue.waiting > 100;
  return (
    <tr className={hasAlert ? 'bg-red-50' : ''}>
      <td className="px-4 py-2.5 text-sm font-mono text-gray-800">{queue.name}</td>
      <td className="px-4 py-2.5 text-sm text-center">
        <span className={queue.waiting > 100 ? 'text-red-600 font-bold' : 'text-gray-600'}>
          {queue.waiting}
        </span>
      </td>
      <td className="px-4 py-2.5 text-sm text-center text-blue-600">{queue.active}</td>
      <td className="px-4 py-2.5 text-sm text-center text-yellow-600">{queue.delayed}</td>
      <td className="px-4 py-2.5 text-sm text-center">
        <span className={queue.failed > 10 ? 'text-red-600 font-bold' : queue.failed > 0 ? 'text-red-400' : 'text-gray-400'}>
          {queue.failed}
        </span>
      </td>
      <td className="px-4 py-2.5 text-sm text-center text-green-600">{queue.completed}</td>
    </tr>
  );
}

function FailedJobRow({ job }: { job: FailedJob }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-4 py-2.5 text-xs font-mono text-gray-500">{job.queue}</td>
        <td className="px-4 py-2.5 text-sm text-gray-700">{job.name}</td>
        <td className="px-4 py-2.5 text-sm text-red-600 max-w-xs truncate">{job.failedReason}</td>
        <td className="px-4 py-2.5 text-xs text-gray-400 whitespace-nowrap">
          {new Date(job.timestamp).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={4} className="px-4 pb-3 bg-gray-50">
            <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap break-all">
              {JSON.stringify(job.data, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}

function ManualTriggerPanel() {
  const [reporterId, setReporterId] = useState('');
  const [publicationId, setPublicationId] = useState('');
  const [reporterMsg, setReporterMsg] = useState('');
  const [publicationMsg, setPublicationMsg] = useState('');

  const enrichReporter = useEnrichReporter();
  const enrichPublication = useEnrichPublication();

  async function handleReporter(e: React.FormEvent) {
    e.preventDefault();
    if (!reporterId.trim()) return;
    try {
      await enrichReporter.mutateAsync(reporterId.trim());
      setReporterMsg('Queued');
      setReporterId('');
    } catch {
      setReporterMsg('Error — check ID');
    }
    setTimeout(() => setReporterMsg(''), 3000);
  }

  async function handlePublication(e: React.FormEvent) {
    e.preventDefault();
    if (!publicationId.trim()) return;
    try {
      await enrichPublication.mutateAsync(publicationId.trim());
      setPublicationMsg('Queued');
      setPublicationId('');
    } catch {
      setPublicationMsg('Error — check ID');
    }
    setTimeout(() => setPublicationMsg(''), 3000);
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h2 className="text-sm font-semibold text-gray-700 mb-4">Manual Enrichment Triggers</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <form onSubmit={handleReporter} className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">Reporter ID (UUID)</label>
            <input
              value={reporterId}
              onChange={(e) => setReporterId(e.target.value)}
              placeholder="e.g. 550e8400-e29b-41d4..."
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
            />
          </div>
          <button
            type="submit"
            disabled={enrichReporter.isPending || !reporterId.trim()}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Enrich Reporter
          </button>
          {reporterMsg && (
            <span className={`text-xs ${reporterMsg === 'Queued' ? 'text-green-600' : 'text-red-600'}`}>
              {reporterMsg}
            </span>
          )}
        </form>

        <form onSubmit={handlePublication} className="flex gap-2 items-end">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 mb-1">Publication ID (UUID)</label>
            <input
              value={publicationId}
              onChange={(e) => setPublicationId(e.target.value)}
              placeholder="e.g. 550e8400-e29b-41d4..."
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
            />
          </div>
          <button
            type="submit"
            disabled={enrichPublication.isPending || !publicationId.trim()}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Enrich Publication
          </button>
          {publicationMsg && (
            <span className={`text-xs ${publicationMsg === 'Queued' ? 'text-green-600' : 'text-red-600'}`}>
              {publicationMsg}
            </span>
          )}
        </form>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { data: queueData, isLoading: queuesLoading } = useQueueStats();
  const { data: failedData, isLoading: failedLoading } = useFailedJobs();
  const { data: coverage, isLoading: coverageLoading } = useCoverageStats();
  const { data: costs, isLoading: costsLoading } = useCostStats();

  const totalFailed = queueData?.queues.reduce((sum, q) => sum + q.failed, 0) ?? 0;
  const totalActive = queueData?.queues.reduce((sum, q) => sum + q.active, 0) ?? 0;
  const totalWaiting = queueData?.queues.reduce((sum, q) => sum + q.waiting, 0) ?? 0;

  return (
    <div className="p-6 max-w-5xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Enrichment Admin</h1>
        <p className="text-sm text-gray-500 mt-0.5">Pipeline health, coverage stats, API costs</p>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          label="Active Jobs"
          value={queuesLoading ? '—' : totalActive}
          sub="across all queues"
          color={totalActive > 0 ? 'text-blue-600' : 'text-gray-900'}
        />
        <StatCard
          label="Waiting"
          value={queuesLoading ? '—' : totalWaiting}
          sub="pending dispatch"
          color={totalWaiting > 100 ? 'text-red-600' : 'text-gray-900'}
        />
        <StatCard
          label="Failed"
          value={queuesLoading ? '—' : totalFailed}
          sub="in dead-letter queues"
          color={totalFailed > 10 ? 'text-red-600' : totalFailed > 0 ? 'text-yellow-600' : 'text-gray-900'}
        />
        <StatCard
          label="Total API Cost"
          value={costsLoading ? '—' : `$${parseFloat(costs?.grandTotal ?? '0').toFixed(4)}`}
          sub="all time"
        />
      </div>

      {/* Queue health table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-700">Queue Health</h2>
        </div>
        {queuesLoading ? (
          <div className="py-8 text-center text-sm text-gray-400">Loading...</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Queue</th>
                <th className="text-center px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Waiting</th>
                <th className="text-center px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Active</th>
                <th className="text-center px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Delayed</th>
                <th className="text-center px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Failed</th>
                <th className="text-center px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Completed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {queueData?.queues.map((q) => <QueueRow key={q.name} queue={q} />)}
            </tbody>
          </table>
        )}
      </div>

      {/* Coverage stats */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">Enrichment Coverage</h2>
        {coverageLoading ? (
          <div className="text-sm text-gray-400">Loading...</div>
        ) : coverage ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Reporters ({coverage.reporters.total.toLocaleString()} total)
              </p>
              <div className="space-y-2.5">
                {[
                  { label: 'Has Email', pct: coverage.reporters.emailPct, value: coverage.reporters.withEmail, color: 'bg-green-500' },
                  { label: 'Enriched', pct: coverage.reporters.enrichedPct, value: coverage.reporters.enriched, color: 'bg-blue-500' },
                  { label: 'Has Bio', pct: coverage.reporters.total > 0 ? Math.round((coverage.reporters.withBio / coverage.reporters.total) * 100) : 0, value: coverage.reporters.withBio, color: 'bg-purple-500' },
                  { label: 'Has Twitter', pct: coverage.reporters.total > 0 ? Math.round((coverage.reporters.withTwitter / coverage.reporters.total) * 100) : 0, value: coverage.reporters.withTwitter, color: 'bg-sky-400' },
                ].map(({ label, pct, value, color }) => (
                  <div key={label}>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>{label}</span>
                      <span className="font-medium">{pct}% ({value.toLocaleString()})</span>
                    </div>
                    <PctBar pct={pct} color={color} />
                  </div>
                ))}
                <div className="flex gap-3 pt-1 text-xs text-gray-400">
                  <span>Pending: {coverage.reporters.pending.toLocaleString()}</span>
                  <span>Failed: <span className={coverage.reporters.failed > 0 ? 'text-red-500' : ''}>{coverage.reporters.failed.toLocaleString()}</span></span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Publications ({coverage.publications.total.toLocaleString()} total)
              </p>
              <div className="space-y-2.5">
                {[
                  { label: 'Has RSS Feed', pct: coverage.publications.rssPct, value: coverage.publications.withRss, color: 'bg-orange-500' },
                  { label: 'Enriched', pct: coverage.publications.enrichedPct, value: coverage.publications.enriched, color: 'bg-blue-500' },
                  { label: 'Email Formats Known', pct: coverage.publications.total > 0 ? Math.round((coverage.publications.withEmailFormats / coverage.publications.total) * 100) : 0, value: coverage.publications.withEmailFormats, color: 'bg-teal-500' },
                ].map(({ label, pct, value, color }) => (
                  <div key={label}>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>{label}</span>
                      <span className="font-medium">{pct}% ({value.toLocaleString()})</span>
                    </div>
                    <PctBar pct={pct} color={color} />
                  </div>
                ))}
                <div className="flex gap-3 pt-1 text-xs text-gray-400">
                  <span>Pending: {coverage.publications.pending.toLocaleString()}</span>
                  <span>Failed: <span className={coverage.publications.failed > 0 ? 'text-red-500' : ''}>{coverage.publications.failed.toLocaleString()}</span></span>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Recent failed jobs */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700">Recent Failures</h2>
          {failedData && (
            <span className="text-xs text-gray-400">{failedData.failedJobs.length} jobs · click to expand data</span>
          )}
        </div>
        {failedLoading ? (
          <div className="py-8 text-center text-sm text-gray-400">Loading...</div>
        ) : !failedData?.failedJobs.length ? (
          <div className="py-8 text-center text-sm text-gray-400">No failed jobs</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Queue</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Job</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Error</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {failedData.failedJobs.map((job, i) => (
                <FailedJobRow key={`${job.queue}-${job.jobId}-${i}`} job={job} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* API cost breakdown */}
      <div className="bg-white rounded-lg border border-gray-200 p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">API Cost Tracking</h2>
        {costsLoading ? (
          <div className="text-sm text-gray-400">Loading...</div>
        ) : !costs ? null : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                External APIs — total ${parseFloat(costs.apiUsage.total).toFixed(4)}
              </p>
              {costs.apiUsage.byType.length === 0 ? (
                <p className="text-xs text-gray-400">No usage logged yet</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-400">
                      <th className="text-left py-1">Provider / Type</th>
                      <th className="text-right py-1">Calls</th>
                      <th className="text-right py-1">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {costs.apiUsage.byType.map((row, i) => (
                      <tr key={i}>
                        <td className="py-1 text-gray-600">{row.provider} / {row.requestType}</td>
                        <td className="py-1 text-right text-gray-500">{row.callCount.toLocaleString()}</td>
                        <td className="py-1 text-right text-gray-700 font-medium">${parseFloat(row.totalCost).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                LLM Calls — total ${parseFloat(costs.llmUsage.total).toFixed(4)}
              </p>
              {costs.llmUsage.byModel.length === 0 ? (
                <p className="text-xs text-gray-400">No usage logged yet</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-400">
                      <th className="text-left py-1">Model / Type</th>
                      <th className="text-right py-1">Calls</th>
                      <th className="text-right py-1">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {costs.llmUsage.byModel.map((row, i) => (
                      <tr key={i}>
                        <td className="py-1 text-gray-600">{row.model} / {row.requestType}</td>
                        <td className="py-1 text-right text-gray-500">{row.callCount.toLocaleString()}</td>
                        <td className="py-1 text-right text-gray-700 font-medium">${parseFloat(row.totalCost).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Manual triggers */}
      <ManualTriggerPanel />
    </div>
  );
}
